import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDateTime, mensajeError } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, Loader2, PlugZap, RefreshCw } from 'lucide-react';

/**
 * WhatsApp → «Conexión» (1/11/2026, solo administradores). Sigue el patrón de
 * «Conexión con Meta» de Campañas, con la Cloud API de Meta (dirección,
 * 1/11/2026: solo Meta; ya no hay que elegir proveedor):
 *  1. los datos de Meta; lo secreto se guarda en el servidor y la pantalla
 *     NUNCA lo vuelve a ver: solo «Puesto ✓ (…últimos 4)»;
 *  2. la dirección del aviso (webhook) y el token de verificación que hay que
 *     poner en Meta;
 *  3. los dos ajustes que decide dirección, apagados por defecto: usar la API
 *     para los envíos automáticos y dejar contestar al asistente.
 * Y «Probar», que le pregunta a Meta SIN MANDAR NADA. Al abrirse no le
 * pregunta nada a Meta.
 */

async function copiar(texto, que) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(`${que} copiado`);
  } catch {
    toast.info(`Selecciona ${que.toLowerCase()} y cópialo a mano`);
  }
}

function Copiable({ valor, que, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="rounded bg-muted px-2 py-1 text-xs break-all select-all" data-testid={testid}>{valor}</code>
      <Button type="button" variant="outline" size="sm" onClick={() => copiar(valor, que)}
        data-testid={`${testid}-copiar`}>
        <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
      </Button>
    </div>
  );
}

function Linea({ etiqueta, children, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-1 sm:gap-2 py-1 text-sm">
      <span className="text-muted-foreground">{etiqueta}</span>
      <span data-testid={testid} className="break-words min-w-0">{children}</span>
    </div>
  );
}

const AYUDAS = {
  meta_token: 'El del usuario del sistema (Configuración del negocio → Usuarios del sistema → Generar token), con permiso de WhatsApp.',
  meta_phone_id: 'En Meta for Developers: WhatsApp → Configuración de la API → «Identificador del número de teléfono». No es el teléfono.',
  meta_app_secret: 'Tu app → Configuración de la app → Básica → «Clave secreta de la app». Sin él no entra ningún aviso.',
  meta_waba_id: 'WhatsApp → Configuración de la API → «Identificador de la cuenta de WhatsApp Business». Hace falta para leer las plantillas.',
};

/** Un dato de la conexión. Lo secreto se pega y no se vuelve a ver. */
function CampoConexion({ ficha, onCambio }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const { campo, etiqueta, secreto, puesto } = ficha;
  const delServidor = ficha.origen === 'entorno';

  const guardar = async () => {
    if (guardando || !valor.trim()) return;
    setGuardando(true);
    try {
      const r = await api.put(`/api/whatsapp/conexion/datos/${campo}`, { valor });
      setValor('');
      setEditando(false);
      onCambio(r.data);
      toast.success(`${etiqueta}: guardado`);
    } catch (e) {
      toast.error(mensajeError(e, `No se ha podido guardar: ${etiqueta}`));
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async () => {
    if (!window.confirm(`¿Quitar «${etiqueta}»? Sin él, la conexión se para.`)) return;
    try {
      const r = await api.delete(`/api/whatsapp/conexion/datos/${campo}`);
      onCambio(r.data);
      toast.success(`${etiqueta}: quitado`);
    } catch (e) {
      toast.error(mensajeError(e, `No se ha podido quitar: ${etiqueta}`));
    }
  };

  if (puesto && !editando) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={`wa-campo-${campo}`}>
        <span className="text-emerald-700 font-medium" data-testid={`wa-campo-${campo}-puesto`}>
          {secreto ? `Puesto ✓${ficha.final ? ` (…${ficha.final})` : ''}` : `${ficha.valor} ✓`}
        </span>
        {delServidor ? (
          <span className="text-xs text-muted-foreground">(lo pone el servidor; si pegas otro aquí, manda el de aquí)</span>
        ) : null}
        <Button type="button" variant="outline" size="sm" onClick={() => setEditando(true)}
          data-testid={`wa-campo-${campo}-cambiar`}>Cambiar</Button>
        {!delServidor ? (
          <Button type="button" variant="ghost" size="sm" onClick={quitar}
            data-testid={`wa-campo-${campo}-quitar`}>Quitar</Button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid={`wa-campo-${campo}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input type={secreto ? 'password' : 'text'} autoComplete="off" spellCheck={false} value={valor}
          onChange={(e) => setValor(e.target.value)} placeholder={`Pega aquí: ${etiqueta}`}
          aria-label={etiqueta} className="max-w-md" data-testid={`wa-campo-${campo}-input`} />
        <Button type="button" size="sm" onClick={guardar} disabled={guardando || !valor.trim()}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={`wa-campo-${campo}-guardar`}>
          {guardando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar
        </Button>
        {puesto ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => { setEditando(false); setValor(''); }}>
            Cancelar
          </Button>
        ) : null}
      </div>
      {AYUDAS[campo] ? <p className="text-xs text-muted-foreground">{AYUDAS[campo]}</p> : null}
    </div>
  );
}

/** Un ajuste de dirección: una casilla con su explicación. */
function Ajuste({ clave, activo, titulo, children, onCambiar, cambiando, at, por }) {
  return (
    <label className="flex items-start gap-3 text-sm py-1.5" data-testid={`wa-ajuste-${clave}`}>
      <input type="checkbox" className="mt-1" checked={!!activo} disabled={cambiando}
        onChange={(e) => onCambiar(clave, e.target.checked)} data-testid={`wa-ajuste-${clave}-casilla`} />
      <span>
        <span className="font-medium text-[#13231E]">{titulo}</span>
        <span className="block text-xs text-muted-foreground">{children}</span>
        {at ? (
          <span className="block text-[11px] text-muted-foreground">
            {activo ? 'Encendido' : 'Apagado'} el {formatDateTime(at)}{por ? ` por ${por}` : ''}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export default function ConexionWhatsApp({ onCambio }) {
  const [estado, setEstado] = useState(null);
  const [fallo, setFallo] = useState('');
  const [probando, setProbando] = useState(false);
  const [cambiando, setCambiando] = useState(false);

  const aplicar = useCallback((datos) => {
    setEstado(datos || null);
    if (onCambio) onCambio(datos || null);
  }, [onCambio]);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/whatsapp/conexion');
      aplicar(r.data);
      setFallo('');
    } catch (e) {
      setFallo(mensajeError(e, 'No se ha podido leer la conexión de WhatsApp'));
    }
  }, [aplicar]);

  useEffect(() => { cargar(); }, [cargar]);

  if (fallo) {
    return (
      <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
        data-testid="wa-conexion-fallo">{fallo}</div>
    );
  }
  if (!estado) {
    return (
      <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando la conexión…
      </div>
    );
  }

  const probar = async () => {
    if (probando) return;
    setProbando(true);
    try {
      const r = await api.post('/api/whatsapp/conexion/probar');
      if (r.data?.ok) toast.success(r.data.texto || 'La conexión funciona');
      else toast.error(r.data?.texto || 'La conexión no funciona');
      setEstado((e) => ({ ...e, ultima_prueba: r.data }));
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido probar la conexión'));
    } finally {
      setProbando(false);
    }
  };

  const cambiarAjuste = async (clave, valor) => {
    if (valor && clave === 'envios_automaticos' && !window.confirm(
      'Con esto, los recordatorios y confirmaciones que se mandan en bloque saldrán SOLOS por la API '
      + 'de WhatsApp (sin abrir los wa.me). Hoy salen como texto libre: a quien no haya escrito en las '
      + 'últimas 24 h WhatsApp se lo rechazará hasta que haya plantillas aprobadas. ¿Encender?')) return;
    if (valor && clave === 'bot_activo' && !window.confirm(
      'Con esto, el asistente contestará DE VERDAD por WhatsApp en las conversaciones que lleva él (hasta ahora '
      + 'solo escribía borradores). ¿Encender?')) return;
    setCambiando(true);
    try {
      const r = await api.put('/api/whatsapp/conexion/ajustes', { [clave]: valor });
      aplicar(r.data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido cambiar el ajuste'));
    } finally {
      setCambiando(false);
    }
  };

  // Solo Meta: sus datos salen aunque el servidor dijera otro proveedor.
  const campos = Object.values(estado.campos || {}).filter((c) => c.proveedor === 'meta');
  const aviso = estado.webhook || {};
  const prueba = estado.ultima_prueba;

  return (
    <section className="cjo-card p-4 space-y-4" data-testid="wa-conexion">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PlugZap className={`w-4 h-4 ${estado.conectado ? 'text-[#2E6E5E]' : 'text-muted-foreground'}`} />
          <span className="font-medium" data-testid="wa-conexion-texto">{estado.texto}</span>
        </div>
        <Button variant="outline" size="sm" onClick={probar} disabled={!estado.conectado || probando}
          data-testid="wa-probar"
          title="Le pregunta a Meta si la conexión va. No manda ningún mensaje.">
          {probando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Probar
        </Button>
      </div>

      <div className="space-y-3 border-t pt-3">
        <div className="text-sm font-medium">1. Datos de Meta</div>
        {campos.map((c) => (
          <div key={c.campo}>
            <div className="text-sm mb-1 first-letter:uppercase">{c.etiqueta}</div>
            <CampoConexion ficha={c} onCambio={aplicar} />
          </div>
        ))}
      </div>

      <div className="border-t pt-3" data-testid="wa-webhook">
        <div className="text-sm font-medium mb-1">2. El aviso de mensajes (webhook)</div>
        <p className="text-xs text-muted-foreground mb-2">
          En Meta for Developers: tu app → WhatsApp → Configuración → Webhook. Pega estos dos datos, pulsa
          «Verificar y guardar» y suscribe el campo «messages». Meta firma cada aviso con el secreto de la app.
        </p>
        <Linea etiqueta="URL de devolución de llamada">
          <Copiable valor={aviso.url} que="La URL" testid="wa-webhook-url" />
        </Linea>
        <Linea etiqueta="Token de verificación">
          {aviso.verify_token
            ? <Copiable valor={aviso.verify_token} que="El token de verificación" testid="wa-webhook-palabra" />
            : '—'}
        </Linea>
        <Linea etiqueta="¿Lo ha comprobado Meta?" testid="wa-webhook-verificado">
          {aviso.verificado_at ? `Sí, el ${formatDateTime(aviso.verificado_at)}` : 'Todavía no'}
        </Linea>
        <Linea etiqueta="Último aviso recibido" testid="wa-ultimo-aviso">
          {estado.ultimo_aviso_at ? formatDateTime(estado.ultimo_aviso_at) : 'Ninguno todavía'}
        </Linea>
      </div>

      <div className="border-t pt-3" data-testid="wa-ajustes">
        <div className="text-sm font-medium mb-1">3. Lo que decide dirección</div>
        <Ajuste clave="envios_automaticos" activo={estado.envios_automaticos} cambiando={cambiando}
          onCambiar={cambiarAjuste} at={estado.envios_automaticos_at} por={estado.envios_automaticos_por}
          titulo="Usar la API de WhatsApp para los envíos automáticos">
          Los recordatorios y confirmaciones en bloque de la agenda. Apagado, siguen saliendo como hasta ahora
          (wa.me uno a uno), aunque el número esté conectado.
        </Ajuste>
        <Ajuste clave="bot_activo" activo={estado.bot_activo} cambiando={cambiando}
          onCambiar={cambiarAjuste} at={estado.bot_activo_at} por={estado.bot_activo_por}
          titulo="El bot puede contestar">
          Apagado, el asistente solo escribe borradores en la bandeja. Encendido, contesta de verdad en las
          conversaciones que lleva él, dentro de las 24 h. Cuando alguien del equipo escribe, el bot se para en esa
          conversación.
        </Ajuste>
      </div>

      {prueba?.at ? (
        <div className="border-t pt-3">
          <Linea etiqueta="Última prueba" testid="wa-ultima-prueba">
            <span className={prueba.ok ? 'text-emerald-700' : 'text-red-700'}>
              {`${formatDateTime(prueba.at)} · ${prueba.texto || ''}`}
            </span>
            {(prueba.detalles || []).map((d) => (
              <span key={d} className="block text-xs">{d}</span>
            ))}
          </Linea>
        </div>
      ) : null}
    </section>
  );
}
